import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'
import type {SortDir} from '@/hooks/common/use-table-state'

export type Employee = components['schemas']['EmployeeRead']
type EmployeeVinculo = components['schemas']['EmployeeVinculo']
export type EmployeeSelfUpdate = components['schemas']['EmployeeSelfUpdate']

export const EMPLOYEES_PAGE_SIZE = 10

export type EmployeeListParams = {
  page: number
  search: string
  sortBy: string
  sortDir: SortDir
  /** Omitirlo devuelve la nómina: el default vive en el backend (`core/nomina.py`) para no listar externos por olvido. */
  vinculo?: EmployeeVinculo
}

export function useEmployees(businessId: string | undefined, params: EmployeeListParams) {
  return useQuery({
    queryKey: ['employees', businessId, params],
    queryFn: async () =>
      (
        await api.get<Page<Employee>>(`/businesses/${businessId}/employees`, {
          params: {
            page: params.page,
            page_size: EMPLOYEES_PAGE_SIZE,
            search: params.search || undefined,
            sort_by: params.sortBy,
            sort_dir: params.sortDir,
            vinculo: params.vinculo,
          },
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

/** Sin paginar; un lado de `vinculo` por llamada y, omitido, la nómina, porque casi todo consumidor cuenta trabajadores. */
export function useAllEmployees(businessId: string | undefined, vinculo?: EmployeeVinculo) {
  return useQuery({
    queryKey: ['employees', businessId, 'all', vinculo ?? 'nomina'],
    queryFn: async () =>
      (await api.get<Employee[]>(`/businesses/${businessId}/employees/all`, {params: {vinculo}})).data,
    enabled: !!businessId,
  })
}

/** Nómina + externos, solo para asignar quién ejecuta una tarea, nunca para contar trabajadores. `includeExternos`
 * difiere el segundo pedido sin paginar hasta que haga falta. */
export function useResponsablesRoster(businessId: string | undefined, includeExternos: boolean) {
  const nomina = useAllEmployees(businessId)
  const externos = useAllEmployees(includeExternos ? businessId : undefined, 'externo')
  const ready = !!nomina.data && (!includeExternos || !!externos.data)
  return {
    data: ready ? [...nomina.data!, ...(includeExternos ? externos.data! : [])] : undefined,
    isLoading: nomina.isLoading || (includeExternos && externos.isLoading),
  }
}

// Owner or the person themself (same RBAC as `get_employee`).
export function useEmployee(businessId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: ['employees', businessId, employeeId],
    queryFn: async () => (await api.get<Employee>(`/businesses/${businessId}/employees/${employeeId}`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

// 404 for a god-mode virtual member, who has no real row, so retries are pointless.
export function useMyEmployee(businessId: string | undefined) {
  return useQuery({
    queryKey: ['employee-me', businessId],
    queryFn: async () => (await api.get<Employee>(`/businesses/${businessId}/employees/me`)).data,
    enabled: !!businessId,
    retry: false,
  })
}

export function useUpdateMyEmployee(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: EmployeeSelfUpdate) =>
      (await api.patch<Employee>(`/businesses/${businessId}/employees/me`, input)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['employee-me', businessId], data)
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
    },
  })
}
